import { StrictMode, useLayoutEffect, useSyncExternalStore } from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";
import {
  createTRPCClient,
  httpBatchLink,
  isTRPCClientError,
  type TRPCClient,
} from "@trpc/client";
import { TRPCProvider, type AppRouter } from "@dmca/frontend/trpc";
import {
  createTRPCOptionsProxy,
  type TRPCOptionsProxy,
} from "@trpc/tanstack-react-query";
import superjson from "@dmca/utils/superjson";
import { theme } from "@dmca/frontend/theme";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { DefaultNotFoundComponent } from "./components/DefaultNotFoundComponent";
import { DefaultPendingComponent } from "./components/DefaultPendingComponent";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import {
  ApplicationUser,
  authClient,
  getAuthCacheBoundary,
  isSessionValid,
  SESSION_QUERY_KEY,
  DarkModeProvider,
  useDarkMode,
} from "@dmca/frontend/hooks";
import { isNetworkError } from "./is-network-error";

export type RouterContext = {
  trpcClient: TRPCClient<AppRouter>;
  trpcQcProxy: TRPCOptionsProxy<AppRouter>;
  qc: QueryClient;
  title?: string;
  user: ApplicationUser;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 11,
    },
    mutations: {
      onError: (error, _variables, _context, mutation) => {
        if (mutation.meta?.suppressGlobalErrorToast) return;
        // Expected business errors (e.g. "duplicate_hardware_id") are
        // handled at the call site with their own UX — don't show the
        // unknown-error toast or report them to Sentry.
        const expectedErrors = mutation.meta?.expectedErrors;
        if (
          Array.isArray(expectedErrors) &&
          isTRPCClientError(error) &&
          expectedErrors.includes(error.message)
        ) {
          return;
        }
        // Handle error globally
        console.error("Error:", error);
        toast.error(
          "An unknown error occurred. Please try again or contact support."
        );
        Sentry.captureException(error);
      },
    },
  },
});

const authCacheBoundary = getAuthCacheBoundary(queryClient);
// Route guards also fetch sessions through React Query. Observe successful
// responses before any child loader can reuse a previous identity's data.
queryClient.getQueryCache().subscribe((event) => {
  if (event.type !== "updated" || event.action.type !== "success") return;
  if (
    event.query.queryKey.length !== SESSION_QUERY_KEY.length ||
    !SESSION_QUERY_KEY.every(
      (part, index) => event.query.queryKey[index] === part
    )
  )
    return;
  const result = event.query.state.data as Awaited<
    ReturnType<typeof authClient.getSession>
  >;
  if (result.error && result.error.status !== 401) return;
  void authCacheBoundary.observeIdentity(
    isSessionValid(result) && result.data?.user ? result.data.user.id : null
  );
});

const trpc = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: (import.meta.env.VITE_API_URL || "") + "/api/trpc", // To avoid build errors when testing
      maxURLLength: 6000, // Needed to avoid header too large (431) errors
      headers: () => {
        const override =
          import.meta.env.DEV &&
          sessionStorage.getItem("dev__permission_override");
        return override ? { "x-dna-dev-permissions": override } : {};
      },
      fetch: (url, options) => {
        return fetch(url, {
          ...(options as RequestInit),
          credentials: "include",
          // Bind batched requests to this document's authenticated lifetime.
          signal:
            options?.signal && AbortSignal.any
              ? AbortSignal.any([options.signal, authCacheBoundary.signal])
              : authCacheBoundary.signal,
        });
      },
      transformer: superjson,
    }),
  ],
});

const trpcQueryClient = createTRPCOptionsProxy({
  queryClient,
  client: trpc,
});

const derivedThemes = {
  light: createTheme(theme.light),
  dark: createTheme(theme.dark),
};

function AppThemeProvider({ children }: { children: React.ReactNode }) {
  const [darkMode] = useDarkMode();
  return (
    <ThemeProvider theme={darkMode ? derivedThemes.dark : derivedThemes.light}>
      {children}
    </ThemeProvider>
  );
}

// Create a new router instance
const router = createRouter({
  routeTree,
  scrollRestoration: true,
  defaultPreloadStaleTime: 0,
  defaultPendingComponent: () => {
    return <DefaultPendingComponent />;
  },
  defaultNotFoundComponent: () => {
    return <DefaultNotFoundComponent />;
  },
  context: {
    trpcClient: trpc,
    trpcQcProxy: trpcQueryClient,
    qc: queryClient,
    user: undefined!,
  },
  Wrap: function WrapComponent({ children }) {
    return (
      <QueryClientProvider client={queryClient}>
        <TRPCProvider trpcClient={trpc} queryClient={queryClient}>
          <DarkModeProvider>
            <AppThemeProvider>{children}</AppThemeProvider>
          </DarkModeProvider>
        </TRPCProvider>
      </QueryClientProvider>
    );
  },
});

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.setTag("stage", import.meta.env.VITE_SST_STAGE);
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    sendDefaultPii: true,
    tunnel: import.meta.env.VITE_API_URL
      ? `${import.meta.env.VITE_API_URL}/api/stt`
      : undefined,
    integrations: [
      Sentry.replayIntegration({
        blockAllMedia: false,
        maskAllText: false,
      }),
      Sentry.tanstackRouterBrowserTracingIntegration(router),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      if (!isNetworkError(hint.originalException)) {
        return event;
      }
      // The device knows it's offline — routine for shop-floor kiosks and
      // not actionable, so don't report it.
      if (!navigator.onLine) {
        return null;
      }
      // Group all API-unreachable failures into a single issue instead of
      // one per transaction/queryFn.
      event.fingerprint = ["api-unreachable-fetch-failure"];
      event.tags = { ...event.tags, network_error: true };
      return event;
    },
  });
}

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function AuthenticatedApp() {
  // Better Auth observes session changes independently of the router cache,
  // including changes from another tab. Temporary errors are not logout.
  const session = authClient.useSession();
  const blocked = useSyncExternalStore(
    authCacheBoundary.subscribe,
    authCacheBoundary.isBlocked,
    authCacheBoundary.isBlocked
  );
  const known =
    !session.isPending &&
    !session.isRefetching &&
    (!session.error || session.error.status === 401);
  const identity =
    !session.error &&
    session.data &&
    session.data.session.expiresAt.valueOf() > Date.now()
      ? session.data.user.id
      : null;
  useLayoutEffect(() => {
    if (known) void authCacheBoundary.observeIdentity(identity);
  }, [known, identity]);
  if (blocked || (known && !authCacheBoundary.canRenderIdentity(identity))) {
    return <DefaultPendingComponent />;
  }
  return <RouterProvider router={router} />;
}

// Render the app
const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <AuthenticatedApp />
    </StrictMode>
  );
}
